import type { ReactElement } from 'react';
import { INK } from './palette.js';

/**
 * The six companions, drawn from scratch. They are Australian-flavoured
 * originals with no relationship to any licensed character - that is a hard
 * rule for this project, and it is why none of them is a blue heeler.
 */

interface CompanionProps {
  companionKey: string | null | undefined;
  size?: number;
  title?: string;
}

export function Companion({ companionKey, size = 96, title }: CompanionProps) {
  const Art = ART[companionKey ?? ''] ?? Quokka;
  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      role="img"
      aria-label={title ?? 'Companion'}
    >
      <Art />
    </svg>
  );
}

const eye = (cx: number, cy: number, r = 4) => (
  <>
    <circle cx={cx} cy={cy} r={r} fill={INK} />
    <circle cx={cx + r * 0.35} cy={cy - r * 0.35} r={r * 0.32} fill="#fff" />
  </>
);

/** Quinn the Quokka - the default face of the app. */
function Quokka() {
  return (
    <>
      <ellipse cx="60" cy="74" rx="34" ry="36" fill="#a9805c" />
      <ellipse cx="60" cy="80" rx="24" ry="26" fill="#e2c39f" />
      <circle cx="32" cy="44" r="12" fill="#a9805c" />
      <circle cx="88" cy="44" r="12" fill="#a9805c" />
      <circle cx="32" cy="44" r="6" fill="#d9a9a0" />
      <circle cx="88" cy="44" r="6" fill="#d9a9a0" />
      <ellipse cx="60" cy="52" rx="30" ry="28" fill="#b98d66" />
      {eye(49, 48)}
      {eye(71, 48)}
      <ellipse cx="60" cy="60" rx="5" ry="4" fill={INK} />
      <path
        d="M48 68a13 13 0 0 0 24 0"
        fill="none"
        stroke={INK}
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle cx="40" cy="62" r="5" fill="#e89684" opacity="0.5" />
      <circle cx="80" cy="62" r="5" fill="#e89684" opacity="0.5" />
    </>
  );
}

/** Rex Rumble - a small dinosaur with a very big roar. */
function Dino() {
  return (
    <>
      <path
        d="M20 96c0-10 6-16 14-18-2-16 10-30 26-30s28 14 26 30c8 2 14 8 14 18Z"
        fill="#5cae72"
      />
      <path d="M46 46l6-14 6 14 6-12 6 12" fill="#f2b134" />
      <ellipse cx="60" cy="72" rx="20" ry="16" fill="#bfe6c6" />
      {eye(50, 56)}
      {eye(70, 56)}
      <path d="M50 70h20" stroke={INK} strokeWidth="3" strokeLinecap="round" />
      <path d="M52 70v5M60 70v5M68 70v5" stroke={INK} strokeWidth="2" />
      <path d="M96 96c8-4 14-12 12-20-6 2-10 6-12 12Z" fill="#5cae72" />
    </>
  );
}

/** Wanda Wombat - plays footy, digs tunnels, never gives up. */
function Wombat() {
  return (
    <>
      <ellipse cx="60" cy="76" rx="36" ry="32" fill="#8d7561" />
      <ellipse cx="60" cy="82" rx="24" ry="22" fill="#c4ac95" />
      <ellipse cx="38" cy="46" rx="10" ry="9" fill="#8d7561" />
      <ellipse cx="82" cy="46" rx="10" ry="9" fill="#8d7561" />
      <ellipse cx="60" cy="54" rx="30" ry="26" fill="#9c8570" />
      {eye(50, 50)}
      {eye(70, 50)}
      <ellipse cx="60" cy="62" rx="7" ry="5" fill={INK} />
      <path
        d="M50 70a12 12 0 0 0 20 0"
        fill="none"
        stroke={INK}
        strokeWidth="3"
        strokeLinecap="round"
      />
      {/* An oval ball, not any club's. */}
      <ellipse cx="94" cy="92" rx="14" ry="9" fill="#b4552f" transform="rotate(-20 94 92)" />
      <path d="M86 92h16" stroke="#fff" strokeWidth="2.5" transform="rotate(-20 94 92)" />
    </>
  );
}

/** Bly the Bilby - sings every answer, sometimes twice. */
function Bilby() {
  return (
    <>
      <ellipse cx="60" cy="80" rx="28" ry="30" fill="#9aa3c4" />
      <ellipse cx="60" cy="86" rx="19" ry="21" fill="#e0e4f2" />
      <ellipse cx="40" cy="34" rx="9" ry="20" fill="#9aa3c4" transform="rotate(-14 40 34)" />
      <ellipse cx="80" cy="34" rx="9" ry="20" fill="#9aa3c4" transform="rotate(14 80 34)" />
      <ellipse cx="40" cy="34" rx="4.5" ry="13" fill="#d9a9c4" transform="rotate(-14 40 34)" />
      <ellipse cx="80" cy="34" rx="4.5" ry="13" fill="#d9a9c4" transform="rotate(14 80 34)" />
      <ellipse cx="60" cy="60" rx="24" ry="22" fill="#aab2d0" />
      {eye(51, 56)}
      {eye(69, 56)}
      <path d="M60 64l-4 5h8Z" fill="#d9738f" />
      <path
        d="M52 74c4 5 12 5 16 0"
        fill="none"
        stroke={INK}
        strokeWidth="3"
        strokeLinecap="round"
      />
      {/* A little song */}
      <path d="M96 44v14a5 5 0 1 1-3-4V48l-8 2v12a5 5 0 1 1-3-4V46Z" fill="#f2b134" />
    </>
  );
}

/** Bolt - a helpful little robot who loves a puzzle. */
function Robot() {
  return (
    <>
      <rect x="28" y="44" width="64" height="56" rx="14" fill="#8fb8d8" />
      <rect x="38" y="56" width="44" height="30" rx="10" fill="#2a3b48" />
      {eye(50, 70, 5)}
      {eye(70, 70, 5)}
      <path d="M52 80h16" stroke="#7fe3c4" strokeWidth="3" strokeLinecap="round" />
      <path d="M60 44V30" stroke="#6f9dbd" strokeWidth="5" strokeLinecap="round" />
      <circle cx="60" cy="26" r="7" fill="#f2b134" />
      <rect x="16" y="60" width="12" height="26" rx="6" fill="#6f9dbd" />
      <rect x="92" y="60" width="12" height="26" rx="6" fill="#6f9dbd" />
      <rect x="42" y="100" width="14" height="12" rx="5" fill="#6f9dbd" />
      <rect x="64" y="100" width="14" height="12" rx="5" fill="#6f9dbd" />
    </>
  );
}

/** Tilly Turtle - slow and steady, and she gets there every time. */
function Turtle() {
  return (
    <>
      <ellipse cx="60" cy="74" rx="40" ry="28" fill="#6fbf9a" />
      <path
        d="M60 50c14 0 26 8 30 20-4 12-16 20-30 20s-26-8-30-20c4-12 16-20 30-20Z"
        fill="#3f8f7d"
      />
      <path d="M60 52v42M34 66l52 0M40 82l40 0" stroke="#2f6f61" strokeWidth="3" opacity="0.6" />
      <ellipse cx="60" cy="40" rx="16" ry="14" fill="#8fd3b4" />
      {eye(53, 38)}
      {eye(67, 38)}
      <path
        d="M54 46a8 8 0 0 0 12 0"
        fill="none"
        stroke={INK}
        strokeWidth="3"
        strokeLinecap="round"
      />
      <ellipse cx="24" cy="92" rx="10" ry="7" fill="#8fd3b4" />
      <ellipse cx="96" cy="92" rx="10" ry="7" fill="#8fd3b4" />
    </>
  );
}

const ART: Record<string, () => ReactElement> = {
  'quinn-quokka': Quokka,
  'rex-rumble': Dino,
  'wanda-wombat': Wombat,
  'bly-bilby': Bilby,
  'bolt-bot': Robot,
  'tilly-turtle': Turtle,
};
